<script setup lang="ts">
import { h, onBeforeMount, onUnmounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import FormHeader from './FormHeader.vue'
import TypeAheadUsers from './TypeAheadUsers.vue'
import { useAPIURLs } from '../composables/useAPIURLs'
import { useOrderItemRules } from '../composables/useOrderItemRules'
import { QInput, QSelect } from 'quasar'

type ReasonOptions = { name: string; label: string }[]

//props
type HeaderProps = { header: string; subheaders: [string, string] }
type InputRenderingProps = {
  name: 'quantity' | 'reason' | 'comment' | 'reps'
  label?: string
}[]
type RenderOrderValues = 'quantity' | 'reason' | 'comment' | 'users'
type LabelValues = 'add' | 'adjust' | 'addToOrder'
type QuantityValues = 'quantity' | 'newQuantity'
type RenderOrderProps = RenderOrderValues[]
type ReasonOptionsProp = { name: string; show: boolean; reason_name: string }[]
type HideFieldsProps = { comment: boolean }
type LabelsProps = {
  action?: LabelValues
  quantity?: QuantityValues
}
const props = withDefaults(
  defineProps<{
    headers: HeaderProps
    data: any
    inputRendering?: InputRenderingProps
    reasonOptions: ReasonOptionsProp
    hideFields?: HideFieldsProps
    renderOrder: RenderOrderProps
    showRemove?: boolean
    allowZero?: boolean
    /**Defaults to "addToOrder" */
    labels?: LabelsProps
  }>(),
  {
    inputRendering: () => [{ name: 'quantity' }, { name: 'reason' }, { name: 'comment' }],
    hideFields: () => {
      return { comment: false }
    },
    showRemove: false,
    allowZero: false,
    labels: () => {
      return { action: 'addToOrder', quantity: 'quantity' }
    },
  }
)
//emit
const emit = defineEmits<{ (e: 'action', data: any): void; (e: 'remove', data: any): void }>()

//translations
const { t } = useI18n({ useScope: 'global' })
const formLabel = (key: string) => t(`form.${key}`)
const formOrderReasons = (key: string) => t(`form.orderReasons.${key}`)
const formError = (key: string) => t(`form.messages.${key}`)

//data
const quantity = ref()
const reason = ref()
const comment = ref()
const reasonOptions = ref<ReasonOptions>([])
const users = ref()
const toRep = ref()
onBeforeMount(() => {
  reasonOptions.value = props.reasonOptions
    .filter((reason) => reason.show)
    .map((reason) => {
      return {
        reason_name: reason.reason_name,
        name: reason.name,
        label: formOrderReasons(reason.name),
      }
    })
  quantity.value = props.data.quantity
})
onUnmounted(() => {
  //remove stale data
  quantity.value = undefined
  reason.value = undefined
  comment.value = undefined
  reasonOptions.value = []
  users.value = undefined
  toRep.value = undefined
})

//validation
const quantityRef = ref()
const reasonRef = ref()
const usersRef = ref()
const { itemNotNull, quantityAboveZero, quantityAboveEqualZero, noDecimalPlace } =
  useOrderItemRules({
    quantity,
    reason,
  })
const validate = () => {
  let quantityValidate = true
  let reasonValidate = true
  let usersValidate = true
  if (quantityRef.value) {
    quantityValidate = quantityRef.value.validate()
  }
  if (reasonRef.value) {
    reasonValidate = reasonRef.value.validate()
  }
  if (usersRef.value) {
    usersValidate = usersRef.value.validate()
    console.log(usersValidate)
  }
  return quantityValidate && reasonValidate && usersValidate
}

const submitOrder = () => {
  //validate
  if (validate()) {
    emit('action', {
      data: props.data,
      quantity:
        reason.value?.name === 'transfer' ? props.data.quantity - quantity.value : quantity.value,
      originalQuantity: props.data.quantity,
      reason: reason.value,
      comment: comment.value,
      users: users.value,
      toRep: toRep.value,
      date: new Date(),
    })
    close.value = true
  }
}
const close = ref(false)
const remove = () => {
  emit('remove', {
    data: props.data,
  })
}
const { API } = useAPIURLs()
type RenderType = { [key in RenderOrderValues]: { condition?: () => boolean; component: any } }
const render: RenderType = {
  comment: {
    component: () =>
      h(QInput, {
        filled: true,
        modelValue: comment,
        hideBottomSpace: true,
        'onUpdate:modelValue': (value: any) => (comment.value = value),
        label: formLabel('comment'),
        type: 'textarea',
      }),
  },
  quantity: {
    component: () =>
      h(QInput, {
        ref: quantityRef,
        filled: true,
        hideBottomSpace: true,
        modelValue: quantity.value,
        'onUpdate:modelValue': (value: any) => (quantity.value = value),
        label:
          reason.value?.name === 'transfer'
            ? 'Transfer quantity'
            : formLabel(props.labels.quantity),
        rules: [
          (val: any) => itemNotNull(val) || formError('fieldNotNull'),
          (val: any) => noDecimalPlace(val) || formError('noDecimal'),
          props.allowZero
            ? (val: any) => quantityAboveEqualZero(val) || formError('notNegative')
            : (val: any) => quantityAboveZero(val) || formError('aboveZero'),
        ],
        type: 'number',
        mask: '###',
      }),
  },
  reason: {
    component: () =>
      h(QSelect, {
        ref: reasonRef,
        filled: true,
        hideBottomSpace: true,
        label: formLabel('reason'),
        modelValue: reason.value,
        'onUpdate:modelValue': (value: any) => {
          reason.value = value
        },
        rules: [(val: any) => itemNotNull(val) || formError('fieldNotNull')],
        lazyRules: true,
        options: reasonOptions.value,
      }),
  },
  users: {
    component: () =>
      h(TypeAheadUsers, {
        ref: usersRef,
        label: 'Rep',
        rules: [(val: any) => itemNotNull(val) || formError('fieldNotNull')],
        hideBottomSpace: true,
        lazyRules: true,
        modelValue: toRep.value,
        'onUpdate:modelValue': (value: any) => (toRep.value = value),
        route: API.value.getAreaStaffingUsers,
      }),
    condition: () => reason.value && reason.value.name === 'transfer',
  },
}
</script>

<template>
  <q-card style="min-width: 280px" class="q-pa-md">
    <form-header :header-text="props.headers.header"></form-header>

    <q-card-section class="q-pt-none">
      <div class="col">
        <span class="text-black tw-font-bold">{{ props.headers.subheaders[0] }}</span>
        <p class="text-black">{{ props.headers.subheaders[1] }}</p>
      </div>
    </q-card-section>
    <template v-for="node in renderOrder">
      <q-card-section
        class="q-pt-none"
        v-if="
          'condition' in render[node] && typeof render[node]['condition'] !== 'undefined'
            ? render[node]['condition']!()
            : true
        "
      >
        <div class="col">
          <component :is="render[node]['component']" />
        </div> </q-card-section
    ></template>

    <q-card-actions align="right" class="text-primary">
      <q-btn flat :label="formLabel('close')" v-close-popup />
      <q-btn
        flat
        no-caps
        class="text-negative"
        label="Remove"
        @click.stop="remove"
        v-if="showRemove && $props.data.quantity < 1"
        v-close-popup
      />
      <q-btn
        color="primary"
        :label="formLabel(labels.action)"
        @click="submitOrder"
        v-close-popup="close"
      />
    </q-card-actions>
  </q-card>
</template>
